import { useEffect, useRef, useState } from 'preact/hooks';

interface UseTypewriterOptions {
  /** 每字基础延迟(ms),默认 45。 */
  baseDelayMs?: number;
  /** 每字随机抖动幅度(±ms),默认 35 —— 让打字速度逐字不同,拟真。 */
  jitterMs?: number;
  /** 遇标点后的额外停顿(ms),默认 180。 */
  punctuationPauseMs?: number;
  /** 为 false 时直接全显(不打字)。默认 true。 */
  enabled?: boolean;
  /** 打字完成回调(全显/降级时也会触发)。 */
  onDone?: () => void;
}

// 中英标点,命中后多停一拍
const PUNCT = /[，。！？、；：,.!?;:]/;

/**
 * 逐字「变速」打字机。
 *
 * - 用递归 setTimeout(而非固定 interval)推进,每字延迟 = base + 随机抖动 + 标点停顿,
 *   因此每个字的速度都不同,接近真人敲字。
 * - text 变化(含 locale 切换)→ 重新打字。
 * - 尊重 prefers-reduced-motion:直接全显并立即 done(仍触发 onDone,避免联动卡住)。
 * - 卸载/重挂在 cleanup 里清定时器;onDone 用 ref 持有,不进 deps。
 */
export function useTypewriter(
  text: string,
  opts: UseTypewriterOptions = {},
): { display: string; done: boolean } {
  const {
    baseDelayMs = 45,
    jitterMs = 35,
    punctuationPauseMs = 180,
    enabled = true,
  } = opts;

  const [display, setDisplay] = useState('');
  const [done, setDone] = useState(false);
  const timerRef = useRef<number | null>(null);
  const onDoneRef = useRef(opts.onDone);
  onDoneRef.current = opts.onDone;

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setDone(false);

    const reduceMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (!enabled || reduceMotion || !text) {
      setDisplay(text);
      setDone(true);
      onDoneRef.current?.();
      return;
    }

    setDisplay('');
    let i = 0;
    const step = () => {
      i += 1;
      setDisplay(text.slice(0, i));
      if (i >= text.length) {
        timerRef.current = null;
        setDone(true);
        onDoneRef.current?.();
        return;
      }
      const ch = text[i - 1];
      const jitter = (Math.random() * 2 - 1) * jitterMs;
      const pause = PUNCT.test(ch) ? punctuationPauseMs : 0;
      const delay = Math.max(8, baseDelayMs + jitter + pause);
      timerRef.current = window.setTimeout(step, delay);
    };
    timerRef.current = window.setTimeout(step, baseDelayMs);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [text, enabled, baseDelayMs, jitterMs, punctuationPauseMs]);

  return { display, done };
}
